import type {
  AgentQuestion,
  NetworkAccessRequest,
} from '@wandb/weave/lib/WbAgent/WbAgentClient';
import React, {createContext, type Dispatch, useContext, useState} from 'react';
import {shallowEqual} from 'react-redux';
import {useSyncExternalStore} from 'use-sync-external-store/shim';
import {useSyncExternalStoreWithSelector} from 'use-sync-external-store/shim/with-selector';

import type {AgentChatAction} from './actions';
import {
  type ActiveThreadLoadState,
  selectActiveThread,
  selectActiveThreadActiveNetworkDomains,
  selectActiveThreadId,
  selectActiveThreadIsCancelling,
  selectActiveThreadLoadState,
  selectActiveThreadPendingNetworkAccessRequest,
  selectActiveThreadPendingQuestions,
  selectActiveThreadTurnOutcome,
  selectIsOnNewChat,
  selectTurnPromptPageUrl,
} from './selectors';
import {type AgentChatStore, createAgentChatStore} from './store';
import type {AgentChatState, RunOutcome, Thread} from './thread';

export type {AgentChatStore};
export type AgentChatDispatch = Dispatch<AgentChatAction>;

// Context

const AgentChatStoreContext = createContext<AgentChatStore | null>(null);

/**
 * useAgentChatStore returns the store handle for non-subscribing access
 * (e.g. reading state inside callbacks). Subscribing consumers should use
 * useAgentChatState, useStoreSelector, or a named selector hook.
 */
export function useAgentChatStore(): AgentChatStore {
  const store = useContext(AgentChatStoreContext);
  if (store == null) {
    throw new Error(
      'useAgentChatStore must be used inside an AgentChatStoreProvider'
    );
  }

  return store;
}

/**
 * useOptionalAgentChatStore returns the store handle, or null when no
 * AgentChatStoreProvider is mounted. Use this when a consumer must tolerate
 * being rendered outside the provider; everywhere else, prefer
 * useAgentChatStore.
 */
export function useOptionalAgentChatStore(): AgentChatStore | null {
  return useContext(AgentChatStoreContext);
}

// Hooks

export function useAgentChatDispatch(): Dispatch<AgentChatAction> {
  return useAgentChatStore().dispatch;
}

/**
 * useAgentChatState subscribes to the entire store and re-renders on every
 * dispatch. Prefer a narrower hook unless the whole state is genuinely needed.
 */
export function useAgentChatState(): AgentChatState {
  const store = useAgentChatStore();
  return useSyncExternalStore(store.subscribe, store.getState);
}

/**
 * useStoreSelector subscribes to a derived slice, re-rendering when the
 * selected value changes (compared via strict equality or, if provided,
 * `isEqual`).
 */
export function useStoreSelector<T>(
  selector: (state: AgentChatState) => T,
  isEqual?: (a: T, b: T) => boolean
): T {
  const store = useAgentChatStore();
  return useSyncExternalStoreWithSelector(
    store.subscribe,
    store.getState,
    null,
    selector,
    isEqual
  );
}

export function useActiveThread(): Thread | undefined {
  return useStoreSelector(selectActiveThread);
}

/**
 * useActiveThreadId returns the sticky `activeThreadId`, which does not rotate
 * when a draft is promoted (see the invariant on {@link AgentChatState}).
 *
 * NOTE: Use it to key UI that must outlive the rotation; anything addressing
 * the thread itself wants `useActiveThread()?.id`.
 */
export function useActiveThreadId(): string | null {
  return useStoreSelector(selectActiveThreadId);
}

export function useActiveThreadLoadState(): ActiveThreadLoadState {
  return useStoreSelector(selectActiveThreadLoadState, shallowEqual);
}

export function useActiveThreadPendingQuestions(): AgentQuestion[] | null {
  return useStoreSelector(selectActiveThreadPendingQuestions);
}

export function useActiveThreadPendingNetworkAccessRequest(): NetworkAccessRequest | null {
  return useStoreSelector(selectActiveThreadPendingNetworkAccessRequest);
}

export function useActiveThreadActiveNetworkDomains(): string[] {
  return useStoreSelector(selectActiveThreadActiveNetworkDomains, shallowEqual);
}

export function useActiveThreadIsCancelling(): boolean {
  return useStoreSelector(selectActiveThreadIsCancelling);
}

/**
 * useActiveThreadTurnOutcome returns the recorded terminal outcome for a turn
 * in the active thread, or undefined when the turn has not settled or `turnId`
 * is null.
 */
export function useActiveThreadTurnOutcome(
  turnId: string | null
): RunOutcome | undefined {
  return useStoreSelector(state =>
    turnId == null ? undefined : selectActiveThreadTurnOutcome(state, turnId)
  );
}

/**
 * useTurnPromptPageUrl returns the page URL captured in the given turn's user
 * prompt within the active thread, or undefined when the turn carried no page
 * context or `turnId` is null.
 */
export function useTurnPromptPageUrl(
  turnId: string | null
): string | undefined {
  return useStoreSelector(state => {
    const threadId = state.activeThreadId;
    return turnId == null || threadId == null
      ? undefined
      : selectTurnPromptPageUrl(state, threadId, turnId);
  });
}

export function useIsOnNewChat(): boolean {
  return useStoreSelector(selectIsOnNewChat);
}

// Provider

export function AgentChatStoreProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [store] = useState(createAgentChatStore);
  return (
    <AgentChatStoreContext.Provider value={store}>
      {children}
    </AgentChatStoreContext.Provider>
  );
}
